<script setup lang="ts">
const colorMode = useColorMode()
colorMode.preference = 'dark'
useHead({
  meta: [ { name: 'viewport', content: 'width=device-width, initial-scale=1' } ],
  link: [ { rel: 'icon', href: '/favicon.ico' } ],
  htmlAttrs: { lang: 'en' }
})

const route = useRoute()

const linkDefinitions = [
  { label: 'Statera', id: '#what-is-statera' },
  { label: 'Deployment', id: '#current-deployment' },
  { label: 'How it works', id: '#how-it-works' },
  { label: 'Status', id: '#development-status' },
  { label: 'Partnership', id: '#partnership' },
  { label: 'Contact', id: '#contact' },
]

const links = computed(() => linkDefinitions.map(({ label, id }) => ({
  label, to: `/${id}`, active: route.hash === id
})))

const title = 'Collor — Connected remote dispensing for veterinary practices'
const description = 'Statera by Collor: solar-powered, cloud-connected dispensers that run autonomously at remote sites. Monitor, configure, and control your entire network from one dashboard.'

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogImage: '/logo.png',
  twitterImage: '/logo.png',
  twitterCard: 'summary_large_image'
})

// Hide header/footer (chrome) on pages that opt out via page meta
const hideChrome = computed(() => route.meta.hideChrome === true)
</script>

<template>
  <UApp>
    <UHeader :to="'/'" class="shadow-2xl">
      <template #title>
        <AppLogo />
      </template>

      <template v-if="!hideChrome" #right>
        <!-- Desktop navigation: simple link-style items -->
        <UNavigationMenu
            :items="links"
            variant="link"
            class="hidden lg:flex"
            :ui="{
              list: 'flex items-center gap-6',
            }"
        />
      </template>

      <template v-if="!hideChrome" #body>
        <!-- Mobile navigation: vertical link-style items -->
        <UNavigationMenu
            :items="links"
            orientation="vertical"
            variant="link"
            class="-mx-2.5"
            :ui="{
              list: 'flex flex-col gap-6',
            }"
        />
      </template>
    </UHeader>

    <UMain>
      <NuxtPage />
    </UMain>

    <!-- Decorative divider and footer hidden along with header when hideChrome is true -->
    <div v-if="!hideChrome" class="h-px bg-gradient-to-r from-transparent via-golden-grass/60 to-transparent"></div>

    <UFooter v-if="!hideChrome" class="bg-bush-900/40 py-12 backdrop-blur-lg shadow-2xl text-marshland-200">
      <template #top>
        <div class="text-center max-w-5xl mx-auto">
          <AppLogo class="mb-4 mx-auto" />

          <p class="text-sm mb-8">
            Connected remote dispensing for veterinary practices
          </p>
        </div>
      </template>

      <template #default>
        <div class="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 text-sm">
          <div class="space-y-2 text-center md:text-left">
            <p class="text-xs font-semibold uppercase tracking-[0.2em] text-golden-grass-400">Company</p>
            <div class="flex flex-col gap-1.5">
              <p>Collor BV</p>
              <p>Beverlosesteenweg 129</p>
              <p>3583 Beringen, Belgium</p>
              <p>BTW BE0801581076</p>
              <p>
                <a
                    href="mailto:info@collor.be"
                    class="text-golden-grass-300 hover:text-golden-grass-200 transition hover:underline"
                >
                  info@collor.be
                </a>
              </p>
            </div>
          </div>

          <div class="space-y-2 text-center md:text-left">
            <p class="text-xs font-semibold uppercase tracking-[0.2em] text-golden-grass-400">Navigation</p>
            <div class="flex flex-col gap-1.5">
              <a
                  v-for="({label, id}) in linkDefinitions"
                  :key="id"
                  :href="id"
                  class="hover:text-golden-grass-300 transition hover:underline">
                {{ label }}
              </a>
            </div>
          </div>

          <div class="space-y-2 text-center md:text-left">
            <p class="text-xs font-semibold uppercase tracking-[0.2em] text-golden-grass-400">Legal</p>
            <div class="flex flex-col gap-1.5">
              <a href="/privacy" class="hover:text-golden-grass-300 hover:underline">
                Privacy Policy
              </a>
              <a href="/terms" class="hover:text-golden-grass-300 hover:underline">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </template>

      <template #bottom>
        <p class="text-xs text-center opacity-60 mt-8">
          © 2025 Collor BV — All rights reserved
        </p>
      </template>
    </UFooter>
  </UApp>
</template>
